"use client";

import { ErrorPageContent } from "@/app/_components/ui/error-page-content";
import { attemptChunkLoadReload } from "@/lib/errors/chunk-load-reload-guard";
import { isTransientRenderError } from "@/lib/errors/is-transient-render-error";
import { getSupportContactForUserType } from "@/lib/errors/support-contact";
import getLogger from "@/lib/logger";
import { SessionProvider, useSession } from "next-auth/react";
import { useEffect, useState } from "react";

const logger = getLogger("global-error");

function _getContactMessage(userType?: string) {
  const contact = getSupportContactForUserType(userType);

  if (contact.kind === "school-success-partner") {
    return "reach out to your school success partner";
  }

  return (
    <>
      reach out to{" "}
      <a
        href={`mailto:${contact.email}`}
        style={{ color: "hsl(198, 69%, 46%)", textDecoration: "underline" }}
      >
        {contact.email}
      </a>
    </>
  );
}

function GlobalErrorContent({ error }: { error: Error & { digest?: string } }) {
  const { data: session, status } = useSession();
  const isTransient = isTransientRenderError(error);
  // Ask for the reload during the first render so nothing flashes before the
  // navigation starts. The guard spends at most one attempt per build, so the
  // repeated call React makes in StrictMode is a no-op.
  const [isReloading] = useState(() => isTransient && attemptChunkLoadReload());

  useEffect(() => {
    if (isTransient) {
      logger.warn(
        {
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
            digest: error.digest,
          },
          userType: session?.user?.userType,
          userId: session?.user?.id,
          sessionStatus: status,
        },
        `Global transient error displayed: ${error.message}`,
      );

      return;
    }

    logger.error(
      {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
          digest: error.digest,
        },
        userType: session?.user?.userType,
        userId: session?.user?.id,
        sessionStatus: status,
      },
      `Global error displayed: ${error.message}`,
    );
  }, [error, isTransient, session, status]);

  // Render nothing while a reload is pending. Once the guard has spent this
  // build's attempt, fall through to the error page rather than leaving the
  // user on a blank screen.
  if (isReloading) {
    return null;
  }

  // Wait for session to resolve so we don't flash the wrong contact message
  if (status === "loading") {
    return null;
  }

  return (
    <ErrorPageContent
      contactMessage={_getContactMessage(session?.user?.userType)}
    />
  );
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  return (
    <html>
      <body style={{ margin: 0 }}>
        <SessionProvider>
          <GlobalErrorContent error={error} />
        </SessionProvider>
      </body>
    </html>
  );
}
